import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Button, Checkbox, Input, Modal, Select } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import DialogLoadState from '../common/DialogLoadState';
import InstallProgress from './InstallProgress';
import { Skeleton } from '../loading';
import ToggleRow from '../common/ToggleRow';
import Guidance from '../common/Guidance';
import PausedRulesNotice from './PausedRulesNotice';
import PublishedBy from '../publishers/PublishedBy';
import { planLimitOf } from '../../utils/planLimit';
import PlanLimitAlert from '../common/PlanLimitAlert';
import { AddOnPreview, InstallPreview, KeptElements, refusalText } from './InstallPreviewNotes';
import { elementRulesAnswerNote, keptFieldsFor } from '../../utils/packageElementRules';
import { canManage } from '../../utils/permissions';
import { isPersonal } from '../../utils/collectionKinds';
import { nestSubCollections, nestedOptionClass } from '../../utils/collectionFamily';
import { normalizeId } from '../../utils/ids';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { legalPublished } from '../../config/legal';
import {
	addLaterExampleCount,
	addOnPreviewNotes,
	addOnsInstalledNote,
	addOnToggleNote,
	addPackageAddOns,
	consentRefusal,
	exampleCountFor,
	getPackageAddOns,
	healthConsentOf,
	installPackage,
	installPreviewNotes,
	installedSettingsNote,
	keyAdjustedNote,
	packSettingsNote,
	formKindWords,
	pausedAutomationsNote,
	packOwnRuleIds,
	pausedRulesNote,
	pausedRulesOf,
	preparedFormsNote,
	presetAddOns,
	previewPackageAddOns,
	previewPackageInstall,
	toggleAddOn,
	PACKAGE_KIND_LABELS,
} from '../../services/packages';
import { useCollections } from '../../contexts/CollectionsContext';
import { droppedFromLinkLine, hasInstallChoices } from '../../utils/installLink';
import ExternalLink from '../common/ExternalLink';
import InstallNeedsPane from './InstallNeedsPane';
import useInstallNeeds from '../../hooks/useInstallNeeds';
import { requirementsOfPackage, usesLines } from '../../utils/packageBindings';

const NEW_COLLECTION = '__new__';

/**
 * When a collection was made, in ms: its `createdAt`, else the moment inside
 * its ObjectId, else 0 (unknown sorts as oldest).
 */
function madeAt(collection) {
	const stamped = Date.parse(collection?.createdAt || collection?.createdOn || '');
	if (Number.isFinite(stamped)) return stamped;
	const id = normalizeId(collection?._id);
	return /^[0-9a-f]{24}$/i.test(id || '') ? parseInt(id.slice(0, 8), 16) * 1000 : 0;
}

/** Newest first; equal or unknown times keep the order the API gave (the sort is stable). */
function newestFirst(collections) {
	return collections.slice().sort((a, b) => madeAt(b) - madeAt(a));
}

/**
 * The picker's search: a collection whose name contains what was typed, in any
 * case. "New collection…" is always offered, so a search that finds nothing
 * still has its way forward.
 */
function filterCollectionOption(input, option) {
	if (option?.value === NEW_COLLECTION) return true;
	return String(option?.title || '').toLowerCase().includes(input.trim().toLowerCase());
}

/**
 * The install dialog (plan §3.3): the package's identity, where it goes, the
 * items switch when it carries items, the guidance nudge on choosing, and the
 * one button. A card or bundle goes into the personal collection (first),
 * a collection the person manages, or a new one; a collection package always
 * makes a new collection, named here. Success lands on the target's home.
 *
 * A bundle's add-ons (keepr-api KPR-383) are checkboxes, the listing's preset
 * (`listingKey`) — or the package's defaults — ticked: ticking one ticks what
 * it needs, and unticking one another ticked add-on needs unticks both, and
 * the dialog says so. When the chosen collection already holds the bundle,
 * the dialog adds add-ons to it instead: those it has are ticked and fixed.
 *
 * `presetTarget` (KPR-364) opens the dialog already on one collection — the
 * pack page's *Add an add-on* on a row of *In your collections* — and falls
 * back to the usual first choice when that collection is not one it can add
 * to (archived, or no longer managed). Absent, every opening starts where it
 * always did.
 *
 * `initialTargetId` opens it on one collection already chosen (the Card
 * Library's bundle group, KPR-1130: Add an add-on).
 *
 * `prefill` (KPR-1439, a link an assistant gave: see utils/installLink.js)
 * opens it with choices made: `collection` (one the person manages, not
 * archived), `newCollection` (a name), `addOns` (keys), `examples`. A choice
 * that no longer fits is dropped and one line says which; the dialog opens
 * either way, and nothing is added until the person presses the button.
 *
 * A health package (keepr-api KPR-478: its Discover category is Health) opens
 * on a consent step of its own — what keepr holds, never sold or shared, who
 * else sees it, not HIPAA-covered (and, once the legal pages publish, a link to
 * the consumer health data page) — and the install form follows
 * only once the person agrees; the version they agreed to rides on the install
 * (or the add-ons), and the API refuses either without it. The words are the
 * server's, so what is shown is what is recorded. Rejected: a checkbox inside
 * the install form — consent to keeping health data is its own act, separate
 * from choosing a collection, and a tick among the add-on ticks reads as one
 * more option. A refusal that says the text changed shows the new text and
 * asks again; nothing was written.
 */
export default function InstallPackageModal({ pkg, listingKey = null, presetTarget = null, initialTargetId = null, prefill = null, open, onClose }) {
	const navigate = useNavigate();
	const { message, notification } = App.useApp();
	const { personalCollectionId } = useCurrentUser();
	const isCollection = pkg?.kind === 'collection';
	const demo = pkg?.contents?.itemsRole === 'demo';
	// A collection package's automatic tags arrive paused (keepr-api tags T10).
	const pausedNote = pausedRulesNote(pkg?.contents?.tagRules || 0);
	// …and every automation and notification a package brings (keepr-api
	// KPR-419 / KPR-361, ruling d30): only the person turns each on.
	const waitingNote = pausedAutomationsNote(pkg?.contents);
	// …and every form it prepares (keepr-api KPR-360): off, no link made.
	const formsNote = preparedFormsNote(pkg?.contents?.forms || 0);
	// A card or bundle package's own filters, launchers, layouts and charts are
	// added to the collection it goes into, never over the person's own
	// (keepr-api KPR-338).
	const settingsNote = isCollection ? '' : packSettingsNote(pkg?.contents);

	// The shared collections list, asked again when the modal opens (a card or
	// bundle only goes into a collection of the person's that exists now).
	// Only an ANSWER counts (`loaded`), never the saved copy a full page load
	// shows before the fetch lands (KPR-1080): this list is where a package
	// goes, so a collection made since that copy was written, a rate-limited
	// fetch or a failed one must show as waiting or as an error with Retry,
	// never as a quietly shorter list (KPR-1236).
	const { collections: sharedCollections, loaded: collectionsLoaded, error: sharedError, refresh: refreshCollections } = useCollections({ refreshOnOpen: open && !isCollection });
	const collections = collectionsLoaded ? sharedCollections : null;
	const collectionsError = !collectionsLoaded && Boolean(sharedError);
	const [target, setTarget] = useState(null);
	const [newName, setNewName] = useState('');
	const [withItems, setWithItems] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	// An install the API is finishing on its own (keepr-api KPR-1655): the run
	// being followed and whether it was one already running (joined), or null.
	const [progress, setProgress] = useState(null);
	const [limitRefusal, setLimitRefusal] = useState(null);
	// A bundle's add-ons (keepr-api KPR-383).
	const addOns = useMemo(() => (!isCollection && Array.isArray(pkg?.addOns) ? pkg.addOns : []), [pkg, isCollection]);
	const hasAddOns = addOns.length > 0;
	const [selected, setSelected] = useState([]);
	const [toggleNote, setToggleNote] = useState('');
	// What the chosen collection holds of this bundle (GET …/add-ons), or null
	// — unknown, a new collection, or an API without add-ons (404): then the
	// dialog installs, as it always did. While it is asked, the ticks wait.
	const [held, setHeld] = useState(null);
	const [checking, setChecking] = useState(false);
	const holdingRef = useRef(false);
	// Health-data consent (keepr-api KPR-478): the version agreed to in this
	// opening of the dialog, and the newer text a refusal handed back.
	const [agreed, setAgreed] = useState(null);
	const [consentNow, setConsentNow] = useState(null);
	const [consentChanged, setConsentChanged] = useState(false);
	// KPR-1439: a link's collection is applied once per opening, when the list answers.
	const linkTargetRef = useRef(false);
	const [linkDropped, setLinkDropped] = useState([]);
	const consent = consentNow || healthConsentOf(pkg);
	const asking = Boolean(consent) && agreed !== consent.version;

	useEffect(() => {
		if (!open) return;
		setError(null);
		setLimitRefusal(null);
		setProgress(null);
		// Asked every time the dialog opens: an agreement is to one install.
		setAgreed(null);
		setConsentNow(null);
		setConsentChanged(false);
		setNewName(pkg?.name || '');
		setTarget(presetTarget || null);
		// Example data is opt-in (keepr-api KPR-465, under KPR-343's ruling):
		// the switch starts off for demo items, every time the dialog opens —
		// examples landing in a person's own collection unasked would be
		// records they never wrote. A package's core records (reference data
		// the cards are built around) stay on.
		setWithItems(pkg?.contents?.itemsRole !== 'demo');
		setToggleNote('');
		setHeld(null);
		setChecking(false);
		holdingRef.current = false;
		let initial = presetAddOns(pkg, listingKey);
		linkTargetRef.current = false;
		const dropped = [];
		if (hasInstallChoices(prefill)) {
			// The link's add-ons replace the tile's; a name this package has no
			// add-on for is dropped, and what a ticked one needs is ticked too.
			const list = Array.isArray(pkg?.addOns) && !isCollection ? pkg.addOns : [];
			if (prefill.addOns.length) {
				const known = prefill.addOns.filter(key => list.some(a => a.key === key));
				const unknown = prefill.addOns.filter(key => !known.includes(key));
				if (unknown.length) dropped.push(list.length ? `the add-on${unknown.length === 1 ? '' : 's'} ${unknown.map(k => `"${k}"`).join(', ')}, which this package does not have` : 'the add-ons, which this package does not have');
				if (list.length) {
					initial = known.reduce((now, key) => toggleAddOn(list, now, key, true).selected, []);
					const also = initial.filter(key => !known.includes(key));
					if (also.length) setToggleNote(`Also ticked ${also.map(k => list.find(a => a.key === k)?.name || k).join(', ')}: the add-ons you chose need ${also.length === 1 ? 'it' : 'them'}.`);
				}
			}
			if (prefill.examples !== null && pkg?.contents?.items) setWithItems(prefill.examples);
			if (isCollection) {
				if (prefill.newCollection) setNewName(prefill.newCollection);
				if (prefill.collection) dropped.push('the collection, because this package makes a new one');
			}
		}
		setLinkDropped(dropped);
		setSelected(initial);
		// Opened for a collection (the Card Library's Add an add-on, KPR-1130):
		// that one is chosen, not the personal collection first.
		if (initialTargetId && !isCollection) setTarget(initialTargetId);
	}, [open, pkg, isCollection, listingKey, presetTarget, initialTargetId, prefill]);

	// Where a card or bundle may go: the personal collection first, then
	// every other collection the person manages (archived ones are frozen),
	// newest first, then a new one. Each sub-collection sits indented beneath
	// its parent. Newest first because the API lists them oldest first and a
	// collection made a minute ago is the one most often wanted (KPR-1236).
	const options = useMemo(() => {
		const managed = (collections || []).filter(c => canManage(c) && c.status !== 'archived');
		const ordered = nestSubCollections([
			...managed.filter(c => isPersonal(c) || normalizeId(c._id) === personalCollectionId),
			...newestFirst(managed.filter(c => !(isPersonal(c) || normalizeId(c._id) === personalCollectionId))),
		]);
		return [
			...ordered.map(({ collection: c, depth }) => ({
				value: normalizeId(c._id),
				label: <RecordBadge kind="collection" record={c} title={c.name} size="xs" />,
				title: c.name,
				className: nestedOptionClass(depth),
			})),
			{ value: NEW_COLLECTION, label: 'New collection…', title: 'New collection' },
		];
	}, [collections, personalCollectionId]);

	useEffect(() => {
		if (target || isCollection || !collections) return;
		// Functional, so a collection picked in the instant between the render
		// that saw no target and this effect is kept: the effect ran with its
		// stale `null` and put the personal collection over the person's choice
		// (found when the preview panel's extra renders moved the timing).
		setTarget(current => current || (options[0] ? options[0].value : NEW_COLLECTION));
	}, [options, target, isCollection, collections]);

	// A link's collection (KPR-1439): chosen once the list answers, when it is
	// one this dialog can add to. Declared after the first-choice effect above
	// so it wins over it. Not in the list yet (a refresh may still be coming,
	// KPR-1236) leaves it waiting, unless the link also named a new collection.
	useEffect(() => {
		if (!open || isCollection || !collections || linkTargetRef.current || !hasInstallChoices(prefill)) return;
		if (prefill.collection && options.some(option => option.value === prefill.collection)) {
			linkTargetRef.current = true;
			setTarget(prefill.collection);
		} else if (prefill.newCollection) {
			linkTargetRef.current = true;
			setTarget(NEW_COLLECTION);
			setNewName(prefill.newCollection);
		}
	}, [open, isCollection, collections, options, prefill]);

	// A preset collection this dialog cannot add to is not kept: the first
	// choice stands in, as for any opening without one (KPR-364).
	useEffect(() => {
		if (!presetTarget || !collections || target !== presetTarget) return;
		if (!options.some(option => option.value === presetTarget)) setTarget(options[0] ? options[0].value : NEW_COLLECTION);
	}, [presetTarget, collections, options, target]);

	const makingNew = isCollection || target === NEW_COLLECTION;

	// Does the chosen collection already hold the bundle? Asked only of a
	// bundle with add-ons, for an existing collection. The answer and the
	// ticks it implies land in ONE update: adding to a collection that has the
	// bundle starts from what it has (ticked and fixed); leaving such a
	// collection goes back to the listing's ticks; any other move keeps the
	// person's. Rejected: an effect that re-ticks after the answer renders —
	// a tick made in between was silently taken back.
	useEffect(() => {
		const settle = (next) => {
			if (next) setSelected(addOns.map(a => a.key).filter(k => next.addOns.some(x => x.key === k && x.installed)));
			else if (holdingRef.current) setSelected(presetAddOns(pkg, listingKey));
			if (next || holdingRef.current) setToggleNote('');
			holdingRef.current = Boolean(next);
			setHeld(next);
		};
		if (!open || !hasAddOns || makingNew) { settle(null); setChecking(false); return undefined; }
		// No collection chosen yet (the list is loading): the ticks wait too.
		if (!target) { setChecking(true); return undefined; }
		let live = true;
		setChecking(true);
		getPackageAddOns(pkg.key, target)
			.then((data) => { if (live) settle(data && data.installed ? data : null); })
			.catch(() => { if (live) settle(null); })
			.finally(() => { if (live) setChecking(false); });
		return () => { live = false; };
	}, [open, hasAddOns, makingNew, target, pkg, addOns, listingKey]);
	const addingLater = Boolean(held);
	const already = useMemo(() => new Set(held ? held.addOns.filter(a => a.installed).map(a => a.key) : []), [held]);
	const toAdd = useMemo(() => selected.filter(key => !already.has(key)), [selected, already]);
	// How many examples this would bring (keepr-api KPR-465): a bundle's core
	// and ticked add-ons in the listing's sets; added later, the add-ons'
	// own, as the collection reports them; any other package, all its items.
	const itemCount = addingLater
		? addLaterExampleCount(held, toAdd)
		: (hasAddOns ? exampleCountFor(pkg, selected, listingKey) : (pkg?.contents?.items || 0));
	const onToggle = (key, checked) => {
		const next = toggleAddOn(addOns, selected, key, checked);
		setSelected(next.selected);
		setToggleNote(addOnToggleNote(addOns, key, checked, next.also));
	};

	const ready = (makingNew ? newName.trim().length > 0 : Boolean(target)) && !checking && (!addingLater || toAdd.length > 0);

	// What adding it to the chosen collection would do, asked of the server
	// before the person presses Add (keepr-api KPR-445): the names it would
	// give, what it would leave out and why. Asked for a collection they
	// already have only — a new one has nothing to clash with — and again when
	// the collection or the ticks change. A collection that already holds the
	// bundle adds add-ons instead, and is asked what adding the ticked ones
	// would do (keepr-api KPR-632), once one is ticked. The answer never gates
	// the button: it is a forecast, and a failed one leaves the person able to add.
	const [forecast, setForecast] = useState({ status: 'idle', data: null, mode: null });
	const [forecastAgain, setForecastAgain] = useState(0);
	// What a bundle's card that can use a card you have uses (keepr-api KPR-1811):
	// the *What it needs* pane and its choices, started over for another
	// collection or other ticks. Once choosing, every preview sends them.
	const needs = useInstallNeeds({ open, resetKey: `${target}|${selected.join(',')}|${addingLater}`, forecast });
	const { bindings, bindingsKey } = needs;
	useEffect(() => {
		if (!open || !pkg || isCollection || makingNew || !target || checking || (addingLater && !toAdd.length)) { setForecast({ status: 'idle', data: null, mode: null }); return undefined; }
		let live = true;
		let timer = null;
		const mode = addingLater ? 'addOns' : 'install';
		const notesOf = addingLater ? addOnPreviewNotes : installPreviewNotes;
		setForecast({ status: 'loading', data: null, mode });
		// Asked with examples only when they are switched on: the shift of their
		// dates (KPR-1064), and how many an add-on would bring, are in the answer then.
		const items = demo && withItems && itemCount ? { items: 'included' } : {};
		const run = () => (addingLater
			? previewPackageAddOns(pkg.key, { collectionId: target, addOns: toAdd, ...items, bindings })
			: previewPackageInstall(pkg.key, {
				target: { collectionId: target },
				...(hasAddOns ? { addOns: selected } : {}),
				...(hasAddOns && listingKey ? { listing: listingKey } : {}),
				...items,
				bindings,
			}))
			.then((data) => { if (live) setForecast(notesOf(data) ? { status: 'ready', data, mode } : { status: 'failed', data: null, mode }); })
			.catch(() => { if (live) setForecast({ status: 'failed', data: null, mode }); });
		// A choice in the pane waits a moment, so a run of choices asks once.
		if (bindingsKey) timer = setTimeout(run, 300); else run();
		return () => { live = false; clearTimeout(timer); };
	}, [open, pkg, isCollection, makingNew, target, checking, addingLater, toAdd, hasAddOns, selected, listingKey, forecastAgain, demo, withItems, itemCount, bindingsKey]); // eslint-disable-line react-hooks/exhaustive-deps
	// A refusal the pane answers (nothing chosen yet) is said as the line below, not as a warning.
	const notes = forecast.status === 'ready' && forecast.mode === 'install' && !needs.needs ? installPreviewNotes(forecast.data) : null;
	// The elements it keeps from some people (keepr-api KPR-1710): the core's
	// and the ticked add-ons' — added later, only the add-ons being added — and
	// what the preview says would not be applied here.
	const keptRules = useMemo(() => {
		const all = Array.isArray(pkg?.elementRules) ? pkg.elementRules : [];
		if (addingLater) return all.filter(rule => rule?.addOn && toAdd.includes(rule.addOn));
		return keptFieldsFor(all, hasAddOns ? new Set(selected) : null);
	}, [pkg, addingLater, toAdd, hasAddOns, selected]);
	const notKept = forecast.status === 'ready' && Array.isArray(forecast.data?.elementRules?.notApplied) ? forecast.data.elementRules.notApplied : [];
	const addOnNotes = forecast.status === 'ready' && forecast.mode === 'addOns' && !needs.needs ? addOnPreviewNotes(forecast.data) : null;
	// What each need uses (plan v4 §5.12): the default's line with Change, or what is still to choose.
	const toChoose = needs.needs || needs.choosing;
	const usesLine = makingNew ? usesLines(requirementsOfPackage(pkg).filter(r => r.kind !== 'provided').map(r => ({ card: r.card, chosen: { how: 'own' } })))
		: (forecast.status === 'ready' && !toChoose ? usesLines(needs.requirements) : []);
	const collectionName = (options.find(o => o.value === target) || {}).title || 'that collection';

	const submit = async () => {
		setBusy(true);
		setError(null);
		setLimitRefusal(null);
		try {
			const healthDataConsent = consent ? agreed : undefined;
			// A large install answers before it is finished (keepr-api KPR-1655):
			// the service follows it to the end, and each step it reports is
			// said here. It resolves to the same answer either way.
			const follow = { onProgress: (run, { joined = false } = {}) => setProgress({ run, joined }) };
			const result = addingLater
				? await addPackageAddOns(pkg.key, { collectionId: target, addOns: toAdd, items: itemCount && withItems ? 'included' : 'none', healthDataConsent, bindings }, follow)
				: await installPackage(pkg.key, {
					healthDataConsent,
					target: makingNew ? { newCollection: { name: newName.trim() } } : { collectionId: target },
					items: itemCount && withItems ? 'included' : 'none',
					...(hasAddOns ? { addOns: selected } : {}),
					// The listing's examples (keepr-api KPR-465): Fleet's vans,
					// not the family car.
					...(hasAddOns && listingKey ? { listing: listingKey } : {}),
					bindings,
				}, follow);
			onClose?.();
			const warnings = Array.isArray(result.warnings) ? result.warnings : [];
			const paused = pausedRulesNote(Array.isArray(result.rules) ? result.rules.length : 0);
			// What it renamed and kept (KPR-338); what it left out is a warning.
			const kept = installedSettingsNote(result.settings);
			// Their own card kept, the package's beside it (KPR-357): said in
			// a notice that stays until closed, as the warnings are, since it
			// names the key the package's card now answers to.
			const adjusted = keyAdjustedNote(result.keyAdjusted);
			// Which add-ons came, and which can come later (KPR-383).
			const withAddOns = addOnsInstalledNote(result.addOns);
			// An element it could not keep as the package says (keepr-api
			// KPR-1710): said in a notice that stays until closed — the element is
			// open to more people than the package meant.
			const rulesNote = elementRulesAnswerNote(result.elementRules);
			const subject = addingLater ? `Add-ons for ${pkg.name}` : pkg.name;
			const verb = addingLater ? 'were added' : 'was added';
			if (warnings.length || rulesNote) {
				notification.warning({
					message: warnings.length
						? `${subject} ${verb}, with ${warnings.length === 1 ? 'one thing' : `${warnings.length} things`} it could not copy`
						: `${subject} ${verb}, but not every element is kept as it says`,
					description: [adjusted, withAddOns, rulesNote, paused, kept, ...warnings.slice(0, 5).map(w => w.message)].filter(Boolean).join(' '),
					duration: 0,
				});
			} else if (adjusted) {
				notification.info({
					message: `${pkg.name} was added beside what you already had`,
					description: [adjusted, withAddOns, kept, paused].filter(Boolean).join(' '),
					duration: 0,
				});
			} else if (kept || withAddOns) {
				// A rename, or what can still be added later, is worth reading,
				// so it stays until closed.
				notification.success({ message: `${subject} ${verb}`, description: [withAddOns, kept, paused].filter(Boolean).join(' '), duration: 0 });
			} else {
				message.success(paused ? `${subject} ${verb}. ${paused}` : `${subject} ${verb}.`);
			}
			// The person's OWN running rules this made act on a value that moves
			// with the date, now paused (KPR-1372): their own notice, apart from
			// the pack's rules above, which it never counts.
			const ownIds = packOwnRuleIds(result);
			const warnedRules = typeof result.automationsWarning === 'string' ? result.automationsWarning.trim() : '';
			if (pausedRulesOf(result.pausedRules, ownIds).total || warnedRules) {
				const pausedKey = `package-paused-${result.collectionId}`;
				notification.warning({
					key: pausedKey,
					message: pausedRulesOf(result.pausedRules, ownIds).total ? 'Some of your rules were paused' : 'Your rules may still be running',
					description: (
						<div className="install-package__waiting">
							<PausedRulesNotice pausedRules={result.pausedRules} ownIds={ownIds} onNavigate={() => notification.destroy(pausedKey)} />
							{warnedRules ? <p>{warnedRules}</p> : null}
						</div>
					),
					duration: 0,
				});
			}
			// What it brought that waits for the person: each by name, linked to
			// the page whose Waiting for you shows what it would do and turns it
			// on — never a switch here.
			const waiting = [
				...(Array.isArray(result.automations) ? result.automations : []).map(rule => ({ ...rule, tab: 'automations' })),
				...(Array.isArray(result.notifications) ? result.notifications : []).map(def => ({ ...def, tab: 'notifications' })),
				// The forms it prepared (KPR-360): each "prepared, off", with its
				// kind, opening the page that makes it live.
				...(Array.isArray(result.forms) ? result.forms : []).map(form => ({ ...form, tab: form.kind === 'public' ? 'public-forms' : 'sharing', isForm: true })),
			];
			if (waiting.length) {
				const key = `package-waiting-${result.collectionId}`;
				const open = (row) => {
					notification.destroy(key);
					navigate(row.path || `/collections/${result.collectionId}/form?tab=${row.tab}`);
				};
				notification.info({
					key,
					message: waiting.length === 1 ? 'One thing waits for you to turn it on' : `${waiting.length} things wait for you to turn them on`,
					description: (
						<div className="install-package__waiting">
							{waiting.some(r => !r.isForm) ? <p>{pausedAutomationsNote({ automations: waiting.filter(r => r.tab === 'automations').length, notifications: waiting.filter(r => r.tab === 'notifications').length })}</p> : null}
							{waiting.some(r => r.isForm) ? <p>{preparedFormsNote(waiting.filter(r => r.isForm).length)}</p> : null}
							<ul>
								{waiting.slice(0, 8).map(row => (
									<li key={row.id || `${row.tab}-${row.name}`}>
										<Button type="link" size="small" onClick={() => open(row)}>{row.name}</Button>
										<span className="meta-line"> · {row.isForm ? `${formKindWords(row.kind)}, prepared, off` : (row.tab === 'notifications' ? 'notification' : 'automation')}</span>
									</li>
								))}
							</ul>
							{waiting.length > 8 ? <p className="meta-line">and {waiting.length - 8} more.</p> : null}
						</div>
					),
					duration: 0,
				});
			}
			navigate(`/collections/${result.collectionId}`);
		} catch (e) {
			// The consent was missing or its text changed (KPR-478): nothing
			// was written; show the text the server holds now and ask again.
			const again = consentRefusal(e);
			if (again) {
				setConsentNow(again.consent);
				setConsentChanged(again.changed);
				setAgreed(null);
			} else if (planLimitOf(e)) {
				setLimitRefusal(planLimitOf(e));
			} else {
				setError(refusalText(e));
				// Their card changed in between (keepr-api KPR-1811): ask again, keeping what still holds.
				if (e?.response?.data?.code === 'stale_card') setForecastAgain(n => n + 1);
			}
		} finally {
			setBusy(false);
			setProgress(null);
		}
	};

	if (!pkg) return null;
	// What the link asked for that could not be used (KPR-1439).
	const linkPieces = [...linkDropped];
	if (prefill && prefill.collection && !isCollection && collections && !options.some(option => option.value === prefill.collection)) {
		linkPieces.unshift('the collection, which you cannot add to or which is archived');
	}
	const linkLine = droppedFromLinkLine(linkPieces);
	const cta = isCollection ? 'Create collection' : 'Add to collection';
	const addText = addingLater ? (toAdd.length === 1 ? 'Add the add-on' : 'Add the add-ons') : cta;
	const okText = toChoose ? 'Choose what it uses' : addText;
	const everything = addingLater && addOns.every(a => already.has(a.key));
	const preview = Array.isArray(pkg.preview) ? pkg.preview : [];
	const identity = (
		<div className="modal-identity">
			<RecordIdentity
				kicker={PACKAGE_KIND_LABELS[pkg.kind] || 'Package'}
				badge={(
					<RecordBadge
						kind={isCollection ? 'collection' : 'card'}
						{...(pkg.kind === 'bundle' && preview.length > 1 ? { records: preview } : { record: { icon: pkg.icon, color: pkg.color, name: pkg.name } })}
						title={pkg.name}
					/>
				)}
				/* Who publishes it (KPR-385, KPR-358): said before anything is
				   added, as on its tile and its page; the mark only when verified. */
				below={pkg.publisher ? <PublishedBy publisher={pkg.publisher} /> : null}
			/>
		</div>
	);

	if (asking) {
		// Its own Modal instance (`key`): a refusal brings this step back
		// while the install form's button is still loading, and one Modal
		// reused across the swap kept that spinner on "I agree".
		return (
			<Modal
				key="consent"
				open={open}
				title={consent.heading}
				okText={consent.agree}
				onOk={() => setAgreed(consent.version)}
				onCancel={onClose}
				destroyOnClose
			>
				{identity}
				<div className="install-package__body install-package__consent">
					{consentChanged ? (
						<Alert type="info" showIcon message="What keepr asks you to agree to has changed since you opened this. Read it again." />
					) : null}
					<ul className="install-package__consent-points">
						{consent.points.map((point, i) => <li key={`${i}:${point}`}>{point}</li>)}
					</ul>
					{/* The page link waits for the legal pages to publish (ruled
					    2026-10-11: until the Oregon LLC exists and the page's text is
					    approved). Two locks: the server's words carry no `page` until
					    then, and this switch is off. */}
					{consent.page && legalPublished() ? (
						<p className="install-package__consent-page"><ExternalLink href={consent.page}>{consent.pageLabel || 'How keepr handles consumer health data'}</ExternalLink></p>
					) : null}
					<p className="meta-line">Nothing is added until you agree, then choose where it goes.</p>
				</div>
			</Modal>
		);
	}

	if (needs.step === 'needs') {
		const blocked = needs.leftLine || (forecast.status !== 'ready' ? 'Checking what it would change…' : (forecast.data?.refusal ? 'It can\'t be added as chosen yet' : ''));
		return (
			<Modal
				key="needs"
				open={open}
				title={cta}
				onCancel={onClose}
				destroyOnClose
				footer={(
					<div className="install-needs__footer">
						{blocked ? <span className="meta-line install-needs__blocked" role="status">{blocked}</span> : null}
						<Button onClick={onClose}>Cancel</Button>
						<Button type="primary" onClick={submit} loading={busy} disabled={Boolean(blocked)}>{addText}</Button>
					</div>
				)}
			>
				{identity}
				<div className="install-package__body">
					{progress ? <InstallProgress run={progress.run} joined={progress.joined} /> : null}
					<InstallNeedsPane
						packageName={pkg.name}
						collectionName={collectionName}
						requirements={needs.requirements}
						choices={needs.choices}
						onChoice={needs.setChoice}
						forecast={forecast}
						onRetry={() => setForecastAgain(n => n + 1)}
						onBack={needs.back}
						leftLine={needs.leftLine}
					/>
					{error ? <Alert type="error" showIcon message={error} className="install-package__error" /> : null}
					<PlanLimitAlert refusal={limitRefusal} className="install-package__error" />
				</div>
			</Modal>
		);
	}

	return (
		<Modal
			key="install"
			open={open}
			title={cta}
			okText={okText}
			onOk={toChoose ? needs.enter : submit}
			onCancel={onClose}
			okButtonProps={{ disabled: !ready || (toChoose && forecast.status !== 'ready'), loading: busy }}
			destroyOnClose
		>
			{identity}

			<div className="install-package__body">
				{progress ? <InstallProgress run={progress.run} joined={progress.joined} /> : null}
				{linkLine ? <Alert type="info" showIcon className="install-package__link-dropped" message={linkLine} role="status" /> : null}
				{isCollection ? (
					<label className="install-package__field">
						<span className="micro-label">Name of the new collection</span>
						<Input value={newName} onChange={e => setNewName(e.target.value)} maxLength={100} aria-label="Name of the new collection" />
					</label>
				) : (
					<>
						<div className="install-package__field">
							<span className="micro-label">Add it to</span>
							<DialogLoadState
								loaded={collections !== null}
								error={collectionsError}
								label="your collections"
								onRetry={() => refreshCollections()}
								skeleton={<Skeleton variant="form-field" count={1} />}
								minHeight={48}
							>
								<Select
									value={target}
									onChange={setTarget}
									options={options}
									optionLabelProp="title"
									showSearch
									filterOption={filterCollectionOption}
									notFoundContent="No collection by that name"
									aria-label="Add it to"
									style={{ width: '100%' }}
								/>
							</DialogLoadState>
						</div>
						{target === NEW_COLLECTION ? (
							<label className="install-package__field">
								<span className="micro-label">Name of the new collection</span>
								<Input value={newName} onChange={e => setNewName(e.target.value)} maxLength={100} aria-label="Name of the new collection" />
							</label>
						) : null}
						<Guidance
							title="Where should it go?"
							why="Your personal collection is a fine home for a card you will use now and then. A collection of its own suits cards that belong together, or that you mean to share."
							links={['discover/install-a-package', 'collections/your-personal-collection', 'collections/create-a-collection']}
							tone="info"
						/>
					</>
				)}

				{itemCount ? (
					<ToggleRow
						first
						label={demo ? `Include ${itemCount === 1 ? 'the example item' : `the ${itemCount} example items`}` : `Include ${itemCount === 1 ? 'the item' : `the ${itemCount} items`}`}
						description={demo
							? (addingLater
								? 'Examples for the add-ons you tick. They show how it works; remove them in one step from the collection\'s home page.'
								: 'They show how it works. Remove them in one step from the collection\'s home page.')
							: 'They are part of the package: reference records the cards are built around.'}
						checked={withItems}
						onChange={setWithItems}
					/>
				) : null}

				{hasAddOns ? (
					<fieldset className="install-package__addons">
						<legend className="micro-label">Add-ons</legend>
						{checking ? <p className="meta-line install-package__held">Checking what that collection already has…</p> : null}
						{addingLater && !checking ? (
							<p className="meta-line install-package__held">
								{everything
									? `That collection already has ${pkg.name} and every add-on.`
									: `That collection already has ${pkg.name}. Tick the add-ons to add to it.`}
							</p>
						) : null}
						{addOns.map(addOn => (
							<Checkbox
								key={addOn.key}
								className="install-package__addon"
								checked={selected.includes(addOn.key)}
								disabled={checking || already.has(addOn.key)}
								onChange={event => onToggle(addOn.key, event.target.checked)}
							>
								<span className="install-package__addon-text">
									<span className="install-package__addon-name">{addOn.name}</span>
									{already.has(addOn.key) || addOn.description ? (
										<span className="install-package__addon-description">{already.has(addOn.key) ? 'Already in this collection' : addOn.description}</span>
									) : null}
								</span>
							</Checkbox>
						))}
						{toggleNote ? <p className="meta-line install-package__addons-note" role="status">{toggleNote}</p> : null}
					</fieldset>
				) : null}

				{forecast.status !== 'idle' ? (
					<section
						className="install-preview"
						aria-label={addingLater ? `What adding the add-ons to ${collectionName} will do` : `What adding it to ${collectionName} will do`}
						aria-live="polite"
						aria-busy={forecast.status === 'loading'}
					>
						{forecast.status !== 'ready' ? (
							<DialogLoadState
								loaded={false}
								loading={forecast.status === 'loading'}
								error={forecast.status === 'failed'}
								errorDetail="There is no preview, but you can still add it."
								label={addingLater ? `what the add-ons would do to ${collectionName}` : `what it would do to ${collectionName}`}
								onRetry={() => setForecastAgain(n => n + 1)}
								skeleton={<Skeleton variant="line" count={2} />}
								minHeight={72}
							/>
						) : null}
						{needs.needs ? needs.requirements.filter(r => !r.chosen || forecast.data?.refusal).map(r => <p key={r.local} className="meta-line install-preview__line">{`${r.card?.name || 'Its card'} can use a card you have. Choose which on the next screen.`}</p>) : null}
						{needs.choosing && !needs.needs ? <p className="meta-line install-preview__line">Your choices for what it uses are kept.</p> : null}
						{notes ? <InstallPreview notes={notes} collectionName={collectionName} showExampleDates={Boolean(demo && withItems && itemCount)} /> : null}
						{addOnNotes ? <AddOnPreview notes={addOnNotes} /> : null}
					</section>
				) : null}
				{usesLine.length ? (
					<p className="meta-line install-needs__uses" data-testid="install-needs-uses">
						{usesLine.join(' ')}
						{makingNew ? null : <Button type="link" size="small" className="install-needs__change" onClick={needs.enter}>Change</Button>}
					</p>
				) : null}
				<KeptElements rules={keptRules} notApplied={notKept} />
				{settingsNote && !notes && !addOnNotes ?<p className="meta-line install-package__settings">{settingsNote}</p> : null}
				{pausedNote ? <p className="meta-line install-package__paused">{pausedNote}</p> : null}
				{waitingNote ? <p className="meta-line install-package__paused">{waitingNote}</p> : null}
				{formsNote ? <p className="meta-line install-package__paused" data-testid="install-package-forms">{formsNote}</p> : null}

				{error ? <Alert type="error" showIcon message={error} className="install-package__error" /> : null}
				<PlanLimitAlert refusal={limitRefusal} className="install-package__error" />
			</div>
		</Modal>
	);
}
